// Zod schemas for every HTTP route and the WebSocket frame envelope of
// the Link v1 API contract and WebSocket protocol.
//
// All request and response bodies that cross the wire MUST be parsed
// through one of these schemas on both sides. Callers should rely on
// `z.infer<typeof X>` for TypeScript types rather than redeclaring the
// shapes by hand.
//
// _Requirements: 8.1, 9.6, 11.4, 15.2_

import { z } from "zod";
import { signupPasskeySchema } from "./passkeys";

import {
  ABUSE_REASONS,
  ACCOUNT_STATUSES,
  CHANNEL_TYPES,
  DM_CHANNEL_TYPES,
  DM_MAX_PARTICIPANTS,
  ENVELOPE_KINDS,
  FILE_MAX_BYTES,
  FRIEND_STATUSES,
  MESSAGE_STORAGE_KINDS,
  MNEMONIC_WORD_COUNT,
  OPERATOR_ACTIONS,
  PLATFORM_KINDS,
  SECRETS_EXPIRATIONS,
  SNIPPET_MAX_BYTES,
  SPACE_TYPES,
  TEXT_MESSAGE_MAX_CHARS,
} from "./constants";

// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------

/**
 * External 12-byte sortable ID rendered as a base32-ish string. We accept
 * a generous character set but bound the length so the schemas catch the
 * most common copy-paste / template-string accidents at the edge.
 */
export const xid = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, "must be url-safe base32/64");
export type Xid = z.infer<typeof xid>;

/** Standard base64 (RFC 4648, with optional padding). */
export const base64 = z
  .string()
  .min(1)
  .regex(/^[A-Za-z0-9+/]+=*$/, "must be base64");
export type Base64 = z.infer<typeof base64>;

/** ISO-8601 timestamp string. */
export const isoTimestamp = z.string().datetime({ offset: true });

/** Unix epoch in milliseconds, integer, non-negative. */
export const epochMs = z.number().int().nonnegative();

/** Stable global account handle. Display names and Space nicknames are separate. */
export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(32)
  .regex(/^[a-z0-9_]+$/, "use lowercase letters, numbers, and underscores only");
export type Username = z.infer<typeof usernameSchema>;

/** Public image URLs may be absolute or served from Link's own API. */
export const imageUrlSchema = z.union([
  z.string().url().max(2048),
  z.string().regex(/^\/v1\/[A-Za-z0-9_/?=&.-]+$/).max(2048),
]);

// ---------------------------------------------------------------------------
// Enum aliases as zod schemas
// ---------------------------------------------------------------------------

export const channelTypeSchema = z.enum(CHANNEL_TYPES);
export const dmChannelTypeSchema = z.enum(DM_CHANNEL_TYPES);
export const spaceTypeSchema = z.enum(SPACE_TYPES);
export const messageStorageKindSchema = z.enum(MESSAGE_STORAGE_KINDS);
export const envelopeKindSchema = z.enum(ENVELOPE_KINDS);
export const platformKindSchema = z.enum(PLATFORM_KINDS);
export const accountStatusSchema = z.enum(ACCOUNT_STATUSES);
export const friendStatusSchema = z.enum(FRIEND_STATUSES);
export const abuseReasonSchema = z.enum(ABUSE_REASONS);
export const operatorActionSchema = z.enum(OPERATOR_ACTIONS);
export const secretsExpirationSchema = z.enum(SECRETS_EXPIRATIONS);

// ---------------------------------------------------------------------------
// Error envelope (shared by HTTP responses and the WebSocket `error` frame)
// ---------------------------------------------------------------------------

export const apiErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string(),
  retryable: z.boolean(),
  fields: z.record(z.string(), z.array(z.string())).optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

// ---------------------------------------------------------------------------
// Auth — Req 1, 2, 3
// ---------------------------------------------------------------------------

export const signupChallengeRequestSchema = z.object({
  public_key_b64: base64,
});
export type SignupChallengeRequest = z.infer<typeof signupChallengeRequestSchema>;

export const signupChallengeResponseSchema = z.object({
  session_id: xid,
  nonce_b64: base64,
  server_time_ms: epochMs,
});
export type SignupChallengeResponse = z.infer<typeof signupChallengeResponseSchema>;

export const signupRequestSchema = z.object({
  passkey_setup: signupPasskeySchema.optional(),
  session_id: xid,
  nonce_b64: base64,
  signature_b64: base64,
  public_key_b64: base64,
  username: usernameSchema,
  display_name: z.string().min(1).max(64).optional(),
  avatar_url: z.string().url().max(2048).optional(),
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

export const sessionEstablishedResponseSchema = z.object({
  account_id: xid,
  session_token: z.string().min(1),
  expires_at: isoTimestamp,
});
export type SessionEstablishedResponse = z.infer<typeof sessionEstablishedResponseSchema>;

export const loginChallengeRequestSchema = signupChallengeRequestSchema;
export type LoginChallengeRequest = z.infer<typeof loginChallengeRequestSchema>;

export const loginChallengeResponseSchema = signupChallengeResponseSchema;
export type LoginChallengeResponse = z.infer<typeof loginChallengeResponseSchema>;

export const loginRequestSchema = z.object({
  session_id: xid,
  nonce_b64: base64,
  signature_b64: base64,
  public_key_b64: base64,
  platform: platformKindSchema,
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const okResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;

export const rotateKeyRequestSchema = z.object({
  new_public_key_b64: base64,
  signature_b64: base64,
});
export type RotateKeyRequest = z.infer<typeof rotateKeyRequestSchema>;

// ---------------------------------------------------------------------------
// Spaces — Req 5, 6, 7, 17
// ---------------------------------------------------------------------------

export const spaceSummarySchema = z.object({
  xid,
  space_type: spaceTypeSchema,
  name: z.string(),
  icon_url: z.string().url().nullable().optional(),
  owner_account_id: xid,
  notification_muted: z.boolean().default(false),
  created_at: isoTimestamp,
});
export type SpaceSummary = z.infer<typeof spaceSummarySchema>;

export const listSpacesResponseSchema = z.object({
  spaces: z.array(spaceSummarySchema),
});
export type ListSpacesResponse = z.infer<typeof listSpacesResponseSchema>;

export const createSpaceRequestSchema = z.object({
  space_type: spaceTypeSchema,
  name: z.string().min(1).max(80),
  icon_url: z.string().url().max(2048).optional(),
});
export type CreateSpaceRequest = z.infer<typeof createSpaceRequestSchema>;

export const patchSpaceRequestSchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    icon_url: z.string().url().max(2048).nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.icon_url !== undefined, {
    message: "at least one field required",
  });
export type PatchSpaceRequest = z.infer<typeof patchSpaceRequestSchema>;

export const transferOwnerRequestSchema = z.object({
  new_owner_account_id: xid,
});
export type TransferOwnerRequest = z.infer<typeof transferOwnerRequestSchema>;

// Members
export const memberSummarySchema = z.object({
  account_id: xid,
  username: usernameSchema,
  display_name: z.string().nullable(),
  nickname: z.string().nullable(),
  avatar_url: imageUrlSchema.nullable(),
  banner_color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  banner_mode: z.enum(["solid", "gradient"]),
  banner_secondary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  pronouns: z.string().max(40).nullable(),
  is_owner: z.boolean(),
  is_admin: z.boolean(),
  joined_at: isoTimestamp,
  presence: z.enum(["online", "away", "busy", "offline"]),
  badges: z.array(z.string()),
  roles: z.array(
    z.object({
      xid,
      name: z.string(),
      color: z.string(),
    }),
  ),
  community_tag: z
    .object({
      xid,
      code: z.string(),
      label: z.string(),
      icon_url: z.string().url().nullable(),
      space_xid: xid,
      space_name: z.string(),
    })
    .nullable(),
});
export type MemberSummary = z.infer<typeof memberSummarySchema>;

export const listMembersResponseSchema = z.object({
  members: z.array(memberSummarySchema),
});
export type ListMembersResponse = z.infer<typeof listMembersResponseSchema>;

export const addMemberRequestSchema = z.object({
  account_id: xid,
});
export type AddMemberRequest = z.infer<typeof addMemberRequestSchema>;

/**
 * Owner/admin-only patch on a member row. Currently the only mutable
 * field exposed over the wire is the admin flag. (Req 6.5)
 */
export const patchMemberRequestSchema = z
  .object({
    is_admin: z.boolean().optional(),
    nickname: z.string().trim().min(1).max(64).nullable().optional(),
  })
  .refine((value) => value.is_admin !== undefined || value.nickname !== undefined, {
    message: "at least one member field is required",
  });
export type PatchMemberRequest = z.infer<typeof patchMemberRequestSchema>;

// Invites
export const createInviteRequestSchema = z.object({
  // 7-day max — cap enforced at the API layer too, but we reject
  // obviously-out-of-range values up front. (Req 6.2)
  expires_in_seconds: z.number().int().positive().max(7 * 24 * 60 * 60),
  max_uses: z.number().int().positive().max(1000).optional(),
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

export const inviteSummarySchema = z.object({
  xid,
  space_xid: xid,
  expires_at: isoTimestamp,
  max_uses: z.number().int().positive().nullable(),
  use_count: z.number().int().nonnegative(),
  created_by: xid,
  created_at: isoTimestamp,
});
export type InviteSummary = z.infer<typeof inviteSummarySchema>;

export const acceptInviteResponseSchema = z.object({
  space: spaceSummarySchema,
});
export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>;

// Sidebar order — Req 17
export const sidebarOrderRequestSchema = z.object({
  space_xids_in_order: z.array(xid).max(1024),
});
export type SidebarOrderRequest = z.infer<typeof sidebarOrderRequestSchema>;

// ---------------------------------------------------------------------------
// Channels — Req 8, 9, 10, 11
// ---------------------------------------------------------------------------

export const channelSummarySchema = z.object({
  xid,
  space_xid: xid,
  channel_type: channelTypeSchema,
  name: z.string().min(1).max(80),
  topic: z.string().max(1024).nullable(),
  sort_position: z.number().int(),
  archived: z.boolean(),
  created_at: isoTimestamp,
});
export type ChannelSummary = z.infer<typeof channelSummarySchema>;

export const listChannelsResponseSchema = z.object({
  channels: z.array(channelSummarySchema),
});
export type ListChannelsResponse = z.infer<typeof listChannelsResponseSchema>;

export const createChannelRequestSchema = z.object({
  channel_type: channelTypeSchema,
  name: z.string().min(1).max(80),
  topic: z.string().max(1024).optional(),
});
export type CreateChannelRequest = z.infer<typeof createChannelRequestSchema>;

export const patchChannelRequestSchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    topic: z.string().max(1024).nullable().optional(),
    sort_position: z.number().int().optional(),
    archived: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.topic !== undefined ||
      v.sort_position !== undefined ||
      v.archived !== undefined,
    { message: "at least one field required" },
  );
export type PatchChannelRequest = z.infer<typeof patchChannelRequestSchema>;

// ---------------------------------------------------------------------------
// Roles — Req 7
// ---------------------------------------------------------------------------

export const roleSummarySchema = z.object({
  xid,
  space_xid: xid,
  name: z.string().min(1).max(64),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  name_style: z.enum(["solid", "gradient", "glow"]),
  secondary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
  sort_position: z.number().int(),
  permissions: z.number().int().nonnegative(),
});
export type RoleSummary = z.infer<typeof roleSummarySchema>;

export const createRoleRequestSchema = z.object({
  name: z.string().min(1).max(64),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  name_style: z.enum(["solid", "gradient", "glow"]).default("solid"),
  secondary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
  permissions: z.number().int().nonnegative(),
});
export type CreateRoleRequest = z.infer<typeof createRoleRequestSchema>;

export const patchRoleRequestSchema = z
  .object({
    name: z.string().min(1).max(64).optional(),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    permissions: z.number().int().nonnegative().optional(),
    sort_position: z.number().int().optional(),
    name_style: z.enum(["solid", "gradient", "glow"]).optional(),
    secondary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.color !== undefined ||
      v.permissions !== undefined ||
      v.sort_position !== undefined ||
      v.name_style !== undefined ||
      v.secondary_color !== undefined,
    { message: "at least one field required" },
  );
export type PatchRoleRequest = z.infer<typeof patchRoleRequestSchema>;

export const setMemberRolesRequestSchema = z.object({
  role_ids: z.array(xid).max(64),
});
export type SetMemberRolesRequest = z.infer<typeof setMemberRolesRequestSchema>;

export const listRolesResponseSchema = z.object({
  roles: z.array(roleSummarySchema),
  default_permissions: z.number().int().nonnegative(),
});
export type ListRolesResponse = z.infer<typeof listRolesResponseSchema>;

export const listInvitesResponseSchema = z.object({
  invites: z.array(inviteSummarySchema),
});
export type ListInvitesResponse = z.infer<typeof listInvitesResponseSchema>;

// ---------------------------------------------------------------------------
// Messages — Req 8, 9, 10, 12
// ---------------------------------------------------------------------------

/**
 * The author summary embedded in a Message read response. Authors that
 * have left a Shared_Workspace are still attributable via
 * `last_known_display_name`. (Req 6.7)
 */
export const messageAuthorSchema = z.object({
  account_id: xid.nullable(),
  display_name: z.string().nullable(),
  avatar_url: imageUrlSchema.nullable(),
});
export type MessageAuthor = z.infer<typeof messageAuthorSchema>;

export const reactionSummarySchema = z.object({
  emoji: z.string().min(1).max(64),
  account_ids: z.array(xid),
});
export type ReactionSummary = z.infer<typeof reactionSummarySchema>;

const messageBaseFields = {
  xid,
  channel_xid: xid,
  author: messageAuthorSchema,
  parent_message_xid: xid.nullable(),
  created_at: isoTimestamp,
  edited_at: isoTimestamp.nullable(),
  deleted_at: isoTimestamp.nullable(),
  expires_at: isoTimestamp.nullable(),
  reactions: z.array(reactionSummarySchema),
} as const;

/** Read-shape for an E2E message: server returns the opaque envelope. */
export const messageE2eReadSchema = z.object({
  ...messageBaseFields,
  storage_kind: z.literal("e2e"),
  envelope_b64: base64,
  envelope_kind: envelopeKindSchema,
  e2e_context_id: z.string().nullable().optional(),
});
export type MessageE2eRead = z.infer<typeof messageE2eReadSchema>;

/** Read-shape for an at-rest message: server has decrypted plaintext. */
export const messageAtRestReadSchema = z.object({
  ...messageBaseFields,
  storage_kind: z.literal("atrest"),
  plaintext: z.string(),
  language_hint: z.string().min(1).max(32).nullable(),
});
export type MessageAtRestRead = z.infer<typeof messageAtRestReadSchema>;

/** Discriminated read-shape returned by both list and detail endpoints. */
export const messageReadSchema = z.discriminatedUnion("storage_kind", [
  messageE2eReadSchema,
  messageAtRestReadSchema,
]);
export type MessageRead = z.infer<typeof messageReadSchema>;

export const listMessagesResponseSchema = z.object({
  messages: z.array(messageReadSchema),
  next_cursor: z.string().nullable(),
});
export type ListMessagesResponse = z.infer<typeof listMessagesResponseSchema>;

export const listMessagesQuerySchema = z.object({
  cursor: z.string().min(1).max(256).optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;

/**
 * Write-shape for the E2E branch of `POST /v1/channels/:xid/messages`.
 * Submitted whenever the target channel is in a Personal_Workspace, a
 * DM, or any Secrets_Channel. The server stores `envelope_b64` verbatim
 * and never decrypts. (Req 8.2, 10.3, 15.3, 15.4)
 */
export const createMessageE2eRequestSchema = z.object({
  storage_kind: z.literal("e2e"),
  envelope_b64: base64,
  envelope_kind: envelopeKindSchema,
  parent_message_xid: xid.optional(),
  /** Required when `envelope_kind === "secrets"`, ignored otherwise. */
  expires_at: isoTimestamp.optional(),
  attachment_xids: z.array(xid).max(10).optional(),
  e2e_context_id: z.string().min(8).max(128).optional(),
});
export type CreateMessageE2eRequest = z.infer<typeof createMessageE2eRequestSchema>;

/**
 * Write-shape for the at-rest branch. Only valid for channels in
 * Shared_Workspaces or Communities whose `channel_type` is not
 * `secrets`. (Req 8.3, 9.6, 22.8)
 */
export const createMessageAtRestRequestSchema = z.object({
  storage_kind: z.literal("atrest"),
  /**
   * Length cap matches the Snippets_Channel byte cap (64 KB) which is
   * the larger of the two channel-type caps. The handler narrows further
   * based on channel_type (4000 chars for text). (Req 8.1, 9.6)
   */
  plaintext: z.string().min(1).max(SNIPPET_MAX_BYTES),
  parent_message_xid: xid.optional(),
  language_hint: z.string().min(1).max(32).optional(),
  mentions: z.array(xid).max(64).optional(),
  attachment_xids: z.array(xid).max(10).optional(),
});
export type CreateMessageAtRestRequest = z.infer<
  typeof createMessageAtRestRequestSchema
>;

/**
 * Discriminated request body for `POST /v1/channels/:xid/messages`.
 * The discriminator is `storage_kind`; the server cross-checks the
 * declared kind against the target channel's space-and-type triple
 * before persisting. (Req 8.2, 8.3, 22.5, 22.8)
 */
export const createMessageRequestSchema = z.discriminatedUnion("storage_kind", [
  createMessageE2eRequestSchema,
  createMessageAtRestRequestSchema,
]);
export type CreateMessageRequest = z.infer<typeof createMessageRequestSchema>;

export const editMessageRequestSchema = createMessageRequestSchema;
export type EditMessageRequest = z.infer<typeof editMessageRequestSchema>;

export const messageCreatedResponseSchema = z.object({
  message: messageReadSchema,
});
export type MessageCreatedResponse = z.infer<typeof messageCreatedResponseSchema>;

// Reactions
export const reactionRequestSchema = z.object({
  emoji: z.string().min(1).max(64),
});
export type ReactionRequest = z.infer<typeof reactionRequestSchema>;

// Pins
export const addPinRequestSchema = z.object({ message_xid: xid });
export type AddPinRequest = z.infer<typeof addPinRequestSchema>;

export const listPinsResponseSchema = z.object({
  pins: z.array(messageReadSchema),
});
export type ListPinsResponse = z.infer<typeof listPinsResponseSchema>;

// ---------------------------------------------------------------------------
// Files / attachments — Req 11
// ---------------------------------------------------------------------------

export const createUploadRequestSchema = z
  .object({
    filename: z.string().min(1).max(512),
    mime: z.string().min(1).max(255),
    byte_size: z.number().int().positive().max(FILE_MAX_BYTES),
    channel_xid: xid,
    folder_path: z.string().max(2048).optional(),
    /**
     * Required for E2E channels (Personal_Workspace / DM); MUST be absent
     * for at-rest channels. The server enforces both halves of the rule
     * after resolving the target channel. (Req 11.5, 11.6)
     */
    e2e_key_envelope_b64: base64.optional(),
    e2e_context_id: z.string().min(8).max(128).optional(),
  })
  .strict();
export type CreateUploadRequest = z.infer<typeof createUploadRequestSchema>;

export const createUploadResponseSchema = z.object({
  upload_id: xid,
  presigned_put_url: z.string().url(),
  expires_at: isoTimestamp,
});
export type CreateUploadResponse = z.infer<typeof createUploadResponseSchema>;

export const finalizeUploadRequestSchema = z.object({
  sha256_b64: base64,
});
export type FinalizeUploadRequest = z.infer<typeof finalizeUploadRequestSchema>;

export const attachmentSummarySchema = z.object({
  xid,
  message_xid: xid.nullable(),
  storage_kind: messageStorageKindSchema,
  filename: z.string(),
  folder_path: z.string().nullable(),
  mime_type: z.string(),
  byte_size: z.number().int().nonnegative(),
  sha256_b64: base64,
  created_at: isoTimestamp,
  finalized_at: isoTimestamp.nullable(),
});
export type AttachmentSummary = z.infer<typeof attachmentSummarySchema>;

export const finalizeUploadResponseSchema = z.object({
  attachment: attachmentSummarySchema,
});
export type FinalizeUploadResponse = z.infer<typeof finalizeUploadResponseSchema>;

// ---------------------------------------------------------------------------
// DMs — Req 15
// ---------------------------------------------------------------------------

export const dmSummarySchema = z.object({
  xid,
  is_group: z.boolean(),
  /** Present only for group conversations. */
  owner_account_id: xid.nullable(),
  /** Optional group name; one-to-one DMs derive their label from the peer. */
  name: z.string().min(1).max(100).nullable(),
  participants: z.array(
    z.object({
      account_id: xid,
      username: z.string().min(1),
      display_name: z.string().nullable(),
      avatar_url: imageUrlSchema.nullable(),
      banner_color: z.string(),
      banner_mode: z.enum(["solid", "gradient"]),
      banner_secondary_color: z.string(),
      pronouns: z.string().nullable(),
      member_since: isoTimestamp,
      badges: z.array(z.string().min(1).max(64)),
      community_tag: memberSummarySchema.shape.community_tag,
      /**
       * Participant's identity public key (65-byte uncompressed
       * SEC1 P-256), base64-encoded. Required for E2E DM sends so
       * the client can encrypt to every recipient (Req 15.3, 15.4).
       */
      public_key_b64: base64,
      /**
       * SHA-256 of `public_key`, base64-encoded. Used as the
       * canonical recipient hash in the E2E envelope.
       */
      account_id_hash_b64: base64,
      presence: z.enum(["online", "away", "busy", "offline"]),
    }),
  ),
  last_read_message_xid: xid.nullable(),
  latest_message_xid: xid.nullable(),
  latest_message_at: isoTimestamp.nullable().optional(),
  unread_count: z.number().int().nonnegative(),
  pinned: z.boolean(),
  muted: z.boolean(),
  created_at: isoTimestamp,
});
export type DmSummary = z.infer<typeof dmSummarySchema>;

export const listDmsResponseSchema = z.object({
  dms: z.array(dmSummarySchema),
});
export type ListDmsResponse = z.infer<typeof listDmsResponseSchema>;

/**
 * The initiator counts toward `DM_MAX_PARTICIPANTS`, so the request can
 * carry at most 9 *other* accounts. (Req 15.2)
 */
export const createDmRequestSchema = z.object({
  participant_account_ids: z
    .array(xid)
    .max(DM_MAX_PARTICIPANTS - 1),
  // Explicit group intent keeps solo/two-person groups distinct from 1:1 DMs.
  is_group: z.boolean().optional(),
  name: z.string().trim().min(1).max(100).optional(),
}).refine((request) => request.is_group === true || request.participant_account_ids.length > 0, {
  message: "Choose a participant, or create a group to start on your own.",
  path: ["participant_account_ids"],
});
export type CreateDmRequest = z.infer<typeof createDmRequestSchema>;

export const createDmResponseSchema = z.object({
  dm: dmSummarySchema,
});
export type CreateDmResponse = z.infer<typeof createDmResponseSchema>;

/**
 * DM messages are always E2E. The body therefore deliberately does not
 * include a discriminator — the server stores envelope bytes verbatim
 * for every DM message. (Req 15.3, 15.4)
 */
export const createDmMessageRequestSchema = z.object({
  envelope_b64: base64,
  envelope_kind: envelopeKindSchema,
  /** No `files` channel type permitted in DMs. (Req 15.5) */
  channel_type: dmChannelTypeSchema,
  parent_message_xid: xid.optional(),
  expires_at: isoTimestamp.optional(),
  attachment_xids: z.array(xid).max(10).optional(),
  e2e_context_id: z.string().min(8).max(128).optional(),
});
export type CreateDmMessageRequest = z.infer<typeof createDmMessageRequestSchema>;

export const updateLastReadRequestSchema = z.object({
  message_xid: xid,
});
export type UpdateLastReadRequest = z.infer<typeof updateLastReadRequestSchema>;

// ---------------------------------------------------------------------------
// Friends and blocks — Req 16
// ---------------------------------------------------------------------------

export const friendRequestRequestSchema = z.object({
  account_id: z.union([xid, usernameSchema]),
});
export type FriendRequestRequest = z.infer<typeof friendRequestRequestSchema>;

export const friendSummarySchema = z.object({
  account_id: xid,
  username: usernameSchema.optional(),
  display_name: z.string().nullable(),
  avatar_url: imageUrlSchema.nullable(),
  banner_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  banner_mode: z.enum(["solid", "gradient"]).optional(),
  banner_secondary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  custom_status: z.string().max(128).nullable().optional(),
  pronouns: z.string().max(40).nullable().optional(),
  member_since: isoTimestamp.optional(),
  badges: z.array(z.string().min(1).max(64)),
  community_tag: memberSummarySchema.shape.community_tag,
  status: friendStatusSchema,
  direction: z.enum(["incoming", "outgoing"]),
  presence: z.enum(["online", "away", "busy", "offline"]),
  since: isoTimestamp,
});
export type FriendSummary = z.infer<typeof friendSummarySchema>;

export const listFriendsResponseSchema = z.object({
  friends: z.array(friendSummarySchema),
});
export type ListFriendsResponse = z.infer<typeof listFriendsResponseSchema>;

export const blockRequestSchema = z.object({
  account_id: xid,
});
export type BlockRequest = z.infer<typeof blockRequestSchema>;

// ---------------------------------------------------------------------------
// Search — Req 13
// ---------------------------------------------------------------------------

export const searchQuerySchema = z.object({
  q: z.string().min(1).max(512),
  space: xid.optional(),
  channel: xid.optional(),
  channel_type: channelTypeSchema.optional(),
  author: xid.optional(),
  from: isoTimestamp.optional(),
  to: isoTimestamp.optional(),
  kind: z.enum(["message", "snippet", "file"]).optional(),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

export const searchHitSchema = z.object({
  message_xid: xid,
  space_xid: xid,
  channel_xid: xid,
  channel_type: channelTypeSchema,
  author_account_xid: xid.nullable(),
  created_at: isoTimestamp,
  snippet_html: z.string(),
  language_hint: z.string().nullable(),
});
export type SearchHit = z.infer<typeof searchHitSchema>;

export const searchResponseSchema = z.object({
  hits: z.array(searchHitSchema),
  total_estimate: z.number().int().nonnegative(),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

// ---------------------------------------------------------------------------
// Account lifecycle — Req 19, 20
// ---------------------------------------------------------------------------

export const exportRequestResponseSchema = z.object({
  export_id: xid,
  status: z.enum(["pending", "in_progress", "ready", "failed"]),
});
export type ExportRequestResponse = z.infer<typeof exportRequestResponseSchema>;

export const exportStatusResponseSchema = z.object({
  export_id: xid,
  status: z.enum(["pending", "in_progress", "ready", "failed"]),
  signed_url: z.string().url().optional(),
  expires_at: isoTimestamp.optional(),
});
export type ExportStatusResponse = z.infer<typeof exportStatusResponseSchema>;

export const deleteAccountRequestSchema = z.object({
  /**
   * Client must retype their own account_id to confirm the destructive
   * action; checked server-side against the session-bound account.
   * (Req 20.1)
   */
  confirm_account_id: xid,
});
export type DeleteAccountRequest = z.infer<typeof deleteAccountRequestSchema>;

export const deleteAccountResponseSchema = z.object({
  ok: z.literal(true),
  scheduled_at: isoTimestamp,
});
export type DeleteAccountResponse = z.infer<typeof deleteAccountResponseSchema>;

// ---------------------------------------------------------------------------
// Operator / admin — Req 23
// ---------------------------------------------------------------------------

export const reportSummarySchema = z.object({
  xid,
  reporter_account_xid: xid,
  reported_message_xid: xid.nullable(),
  reported_account_xid: xid.nullable(),
  reason: abuseReasonSchema,
  note: z.string().nullable(),
  created_at: isoTimestamp,
  resolved_at: isoTimestamp.nullable(),
  resolved_by_account_xid: xid.nullable(),
});
export type ReportSummary = z.infer<typeof reportSummarySchema>;

export const listReportsResponseSchema = z.object({
  reports: z.array(reportSummarySchema),
});
export type ListReportsResponse = z.infer<typeof listReportsResponseSchema>;

export const reportMessageRequestSchema = z.object({
  reason: abuseReasonSchema,
  note: z.string().max(2048).optional(),
});
export type ReportMessageRequest = z.infer<typeof reportMessageRequestSchema>;

export const resolveReportRequestSchema = z.object({
  resolution: z.enum(["dismissed", "actioned"]),
  note: z.string().max(2048).optional(),
});
export type ResolveReportRequest = z.infer<typeof resolveReportRequestSchema>;

/**
 * Admin message-detail response. NEVER decrypts E2E content; the E2E
 * branch carries metadata and the opaque envelope only. (Req 23.6,
 * Property 6)
 */
export const adminMessageE2eDetailSchema = z.object({
  storage_kind: z.literal("e2e"),
  message_xid: xid,
  channel_xid: xid,
  space_xid: xid.nullable(),
  dm_xid: xid.nullable(),
  author_account_xid: xid.nullable(),
  created_at: isoTimestamp,
  envelope_kind: envelopeKindSchema,
  envelope_b64: base64,
});
export type AdminMessageE2eDetail = z.infer<typeof adminMessageE2eDetailSchema>;

export const adminMessageAtRestDetailSchema = z.object({
  storage_kind: z.literal("atrest"),
  message_xid: xid,
  channel_xid: xid,
  space_xid: xid,
  author_account_xid: xid.nullable(),
  created_at: isoTimestamp,
  plaintext: z.string(),
  language_hint: z.string().nullable(),
});
export type AdminMessageAtRestDetail = z.infer<typeof adminMessageAtRestDetailSchema>;

export const adminMessageDetailSchema = z.discriminatedUnion("storage_kind", [
  adminMessageE2eDetailSchema,
  adminMessageAtRestDetailSchema,
]);
export type AdminMessageDetail = z.infer<typeof adminMessageDetailSchema>;

export const banAccountRequestSchema = z.object({
  reason: z.string().min(1).max(1024),
});
export type BanAccountRequest = z.infer<typeof banAccountRequestSchema>;

export const legalExportRequestSchema = z.object({
  account_xid: xid,
  scope: z.enum(["permitted_fields"]),
});
export type LegalExportRequest = z.infer<typeof legalExportRequestSchema>;

export const legalExportResponseSchema = z.object({
  account_xid: xid,
  public_key_b64: base64,
  account_id_hash_b64: base64,
  created_at: isoTimestamp,
  last_session_ip: z.union([z.ipv4(), z.ipv6()]).nullable(),
  shared_workspace_message_metadata: z.array(
    z.object({
      message_xid: xid,
      space_xid: xid,
      channel_xid: xid,
      created_at: isoTimestamp,
    }),
  ),
});
export type LegalExportResponse = z.infer<typeof legalExportResponseSchema>;

// ---------------------------------------------------------------------------
// WebSocket frames — design.md "WebSocket Protocol"
// ---------------------------------------------------------------------------

/**
 * Whitelist of `event_type` values the realtime layer emits. Mirrored
 * from `realtime_outbox.event_type` in design.md.
 */
export const wsEventTypeSchema = z.enum([
  "message.created",
  "message.edited",
  "message.deleted",
  "dm.updated",
  "dm.created",
  "dm.message_created",
  "dm.participant_added",
  "dm.participant_removed",
  "reaction.added",
  "reaction.removed",
  "pin.added",
  "pin.removed",
  "mention.received",
  "space.member_added",
  "space.member_updated",
  "space.member_removed",
  "space.role_changed",
  "space.role_created",
  "space.role_updated",
  "space.role_deleted",
  "space.member_roles_updated",
  "space.invite_created",
  "space.invite_revoked",
  "space.member_banned",
  "space.member_unbanned",
  "space.audit_created",
  "space.emoji_created",
  "space.emoji_deleted",
  "space.updated",
  "community_tag.updated",
  "release.announced",
  "notification.created",
  "notification.read",
  "profile.updated",
  "friend.request_received",
  "friend.request_accepted",
  "friend.updated",
  "call.incoming",
  "call.updated",
  "call.missed",
  "presence.online",
  "presence.offline",
  "session.kicked",
]);
export type WsEventType = z.infer<typeof wsEventTypeSchema>;

const wsHelloSchema = z.object({
  t: z.literal("hello"),
  client_time_ms: epochMs,
  last_event_id: z.number().int().nonnegative().optional(),
});

const wsReadySchema = z.object({
  t: z.literal("ready"),
  server_time_ms: epochMs,
  session_xid: xid,
});

const wsSubscribeSchema = z.object({
  t: z.literal("subscribe"),
  topics: z.array(z.string().min(1).max(256)).min(1).max(256),
});

const wsSubscribedSchema = z.object({
  t: z.literal("subscribed"),
  topics: z.array(z.string().min(1).max(256)).max(256),
});

const wsUnsubscribeSchema = z.object({
  t: z.literal("unsubscribe"),
  topics: z.array(z.string().min(1).max(256)).min(1).max(256),
});

const wsAckSchema = z.object({
  t: z.literal("ack"),
  event_id: z.number().int().nonnegative(),
});

const wsEventSchema = z.object({
  t: z.literal("event"),
  event_id: z.number().int().nonnegative(),
  topic: z.string().min(1).max(256),
  type: wsEventTypeSchema,
  /**
   * Public-safe payload. Per the design, payloads NEVER contain plaintext
   * from Personal_Workspaces, Secrets_Channels, or DMs; for E2E content
   * they carry the opaque envelope bytes for client-side decryption.
   */
  payload: z.unknown(),
  replayed: z.boolean().optional(),
});

const wsResyncRequiredSchema = z.object({
  t: z.literal("resync_required"),
  reason: z.enum(["too_old", "subscription_changed"]),
});

const wsErrorSchema = z.object({
  t: z.literal("error"),
  code: z.string().min(1),
  message: z.string(),
});

/**
 * Discriminated union of every frame that may flow in either direction
 * over the WebSocket. The `t` field is the discriminator. (design.md
 * "Envelope")
 */
export const wsFrameSchema = z.discriminatedUnion("t", [
  wsHelloSchema,
  wsReadySchema,
  wsSubscribeSchema,
  wsSubscribedSchema,
  wsUnsubscribeSchema,
  wsAckSchema,
  wsEventSchema,
  wsResyncRequiredSchema,
  wsErrorSchema,
]);
export type WsFrame = z.infer<typeof wsFrameSchema>;

export type WsHelloFrame = z.infer<typeof wsHelloSchema>;
export type WsReadyFrame = z.infer<typeof wsReadySchema>;
export type WsSubscribeFrame = z.infer<typeof wsSubscribeSchema>;
export type WsUnsubscribeFrame = z.infer<typeof wsUnsubscribeSchema>;
export type WsAckFrame = z.infer<typeof wsAckSchema>;
export type WsEventFrame = z.infer<typeof wsEventSchema>;
export type WsResyncRequiredFrame = z.infer<typeof wsResyncRequiredSchema>;
export type WsErrorFrame = z.infer<typeof wsErrorSchema>;

/**
 * Runtime guard for an unknown JSON value. Returns a typed `WsFrame`
 * when the value matches the discriminated union, otherwise `null`.
 * Use this on every inbound frame in both the API gateway and the web
 * client before dispatching on `frame.t`.
 */
export function isWsFrame(value: unknown): value is WsFrame {
  return wsFrameSchema.safeParse(value).success;
}

/**
 * Runtime guard variant that also returns the parsed value, so callers
 * who already need the typed object don't pay the cost of parsing twice.
 */
export function parseWsFrame(value: unknown):
  | { ok: true; frame: WsFrame }
  | { ok: false; error: z.ZodError } {
  const result = wsFrameSchema.safeParse(value);
  if (result.success) {
    return { ok: true, frame: result.data };
  }
  return { ok: false, error: result.error };
}

// ---------------------------------------------------------------------------
// Convenience guards used by Req 8.1 (text vs snippet length) — keeps the
// magic numbers next to the data shapes that consume them.
// ---------------------------------------------------------------------------

/**
 * Narrowing length check for `text` channel plaintext: 4000 UTF-8
 * characters maximum, counted as code points. (Req 8.1)
 */
export function isWithinTextMessageLimit(plaintext: string): boolean {
  // Counting code points rather than UTF-16 code units matches the
  // requirement's "4000 characters" wording.
  let count = 0;
  for (const _ of plaintext) {
    count++;
    if (count > TEXT_MESSAGE_MAX_CHARS) return false;
  }
  return true;
}

/**
 * Narrowing byte-length check for `snippets` channel plaintext: 64 KB
 * encoded as UTF-8. (Req 9.6)
 *
 * Implemented in plain JavaScript so the helper does not depend on a
 * `TextEncoder` global (the shared package targets `lib: ["ES2023"]`
 * without DOM/Node types).
 */
export function isWithinSnippetByteLimit(plaintext: string): boolean {
  let bytes = 0;
  for (let i = 0; i < plaintext.length; i++) {
    const code = plaintext.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // High surrogate; the matching low surrogate (the next char) makes
      // a 4-byte UTF-8 sequence. Skip the low surrogate so we don't
      // double-count it as a 3-byte BMP char.
      bytes += 4;
      i += 1;
    } else {
      bytes += 3;
    }
    if (bytes > SNIPPET_MAX_BYTES) return false;
  }
  return true;
}

/**
 * Sanity check on a recovered Recovery_Mnemonic word count before
 * touching the BIP-39 wordlist or HKDF pipeline. (Req 1.1, Req 3.1)
 */
export function hasMnemonicWordCount(phrase: string): boolean {
  return phrase.trim().split(/\s+/u).length === MNEMONIC_WORD_COUNT;
}
